import type { Circuit } from '@multirx/shared/types/track.js';
import { TrackCellType } from '@multirx/shared/types/track.js';
import { getCircuitDisplayBounds } from '@multirx/shared/game/circuitEdit.js';
import { drawCircuitCellEdges } from '@multirx/shared/game/circuitEdgeDraw.js';
import { drawCircuitProps } from '@multirx/shared/game/circuitPropDraw.js';
import { getDecorCheckerHex } from '@multirx/shared/game/circuitDecorStyle.js';
import {
  drawDamierCellFill,
  isDamierSurface,
  isSolidCircuitSurface,
  SURFACE_FILL_HEX,
} from '@multirx/shared/game/circuitSurfaceStyle.js';
export type GridStyle = 'light' | 'path-only' | 'checker' | 'wire' | 'highlight';

/** Taille d'une case en unités monde — GameScene / ArchiveReplayScene. */
export const GAME_TRACK_CELL_SIZE = 128;

/** Marge de cadrage auto (fitCameraToCircuit, hors course). */
export const TRACK_FIT_PADDING = 0.9;

export const PATH_CELL_RATIO = 0.8;
export const BEZIER_OFFSET = 0.35;
export const STRAIGHT_LINE_COLOR = 0x475569;
export const TURN_LINE_COLOR = 0xca8a04;
export const JOKER_LAP_FILL = 0x7c3aed;
export const JOKER_LAP_STROKE = 0xc4b5fd;
export const START_LANE_FILL = 0xfbbf24;
export const START_LANE_STROKE = 0xfef3c7;

export type TrackRenderParams = {
  offsetX: number;
  offsetY: number;
  cellSize: number;
  gridStyle?: GridStyle;
  /** Défaut true — désactivé en course Phaser (sprites dédiés). */
  drawVehicles?: boolean;
};

/** Surface de dessin — Phaser Graphics ou Canvas 2D. */
export interface TrackDrawSurface {
  lineStyle(width: number, color: number, alpha?: number): void;
  fillStyle(color: number, alpha?: number): void;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  strokePath(): void;
  fillRect(x: number, y: number, w: number, h: number): void;
  strokeRect(x: number, y: number, w: number, h: number): void;
  strokeCircle(x: number, y: number, radius: number): void;
}

export function calculateTrackOffset(
  circuit: Circuit,
  cellSize: number,
  screenWidth: number,
  screenHeight: number
): { offsetX: number; offsetY: number } {
  const bounds = getCircuitDisplayBounds(circuit);
  if (!bounds) {
    return { offsetX: screenWidth / 2, offsetY: screenHeight / 2 };
  }
  const centerPxX = ((bounds.minX + bounds.maxX + 1) / 2) * cellSize;
  const centerPxY = ((bounds.minY + bounds.maxY + 1) / 2) * cellSize;
  return {
    offsetX: screenWidth / 2 - centerPxX,
    offsetY: screenHeight / 2 - centerPxY,
  };
}

export function calculateTrackZoomFit(
  circuit: Circuit,
  cellSize: number,
  screenWidth: number,
  screenHeight: number,
  padding = TRACK_FIT_PADDING
): number {
  const bounds = getCircuitDisplayBounds(circuit);
  if (!bounds) return 1;
  const circuitW = bounds.widthCells * cellSize;
  const circuitH = bounds.heightCells * cellSize;
  return Math.min((screenWidth / circuitW) * padding, (screenHeight / circuitH) * padding);
}

function drawQuadratic(
  surface: TrackDrawSurface,
  x0: number,
  y0: number,
  cx: number,
  cy: number,
  x1: number,
  y1: number
): void {
  const steps = 16;
  for (let k = 1; k <= steps; k++) {
    const t = k / steps;
    const u = 1 - t;
    const x = u * u * x0 + 2 * u * t * cx + t * t * x1;
    const y = u * u * y0 + 2 * u * t * cy + t * t * y1;
    surface.lineTo(x, y);
  }
}

function drawGrid(
  surface: TrackDrawSurface,
  circuit: Circuit,
  offsetX: number,
  offsetY: number,
  cellSize: number,
  gridStyle: GridStyle
): void {
  const { width, height, path } = circuit;

  switch (gridStyle) {
    case 'light': {
      surface.lineStyle(1, 0x94a3b8, 0.4);
      for (let x = 0; x <= width; x++) {
        const px = offsetX + x * cellSize;
        surface.beginPath();
        surface.moveTo(px, offsetY);
        surface.lineTo(px, offsetY + height * cellSize);
        surface.strokePath();
      }
      for (let y = 0; y <= height; y++) {
        const py = offsetY + y * cellSize;
        surface.beginPath();
        surface.moveTo(offsetX, py);
        surface.lineTo(offsetX + width * cellSize, py);
        surface.strokePath();
      }
      break;
    }
    case 'path-only': {
      surface.lineStyle(1, 0x94a3b8, 0.6);
      for (const cell of path) {
        const x = offsetX + cell.x * cellSize;
        const y = offsetY + cell.y * cellSize;
        surface.strokeRect(x, y, cellSize, cellSize);
      }
      break;
    }
    case 'checker': {
      surface.lineStyle(0, 0, 0);
      for (let gy = 0; gy < height; gy++) {
        for (let gx = 0; gx < width; gx++) {
          if ((gx + gy) % 2 === 0) {
            surface.fillStyle(0xffffff, 0.04);
            surface.fillRect(offsetX + gx * cellSize, offsetY + gy * cellSize, cellSize, cellSize);
          }
        }
      }
      break;
    }
    case 'wire': {
      surface.lineStyle(0.5, 0xcbd5e1, 0.6);
      for (let x = 0; x <= width; x++) {
        const px = offsetX + x * cellSize;
        surface.beginPath();
        surface.moveTo(px, offsetY);
        surface.lineTo(px, offsetY + height * cellSize);
        surface.strokePath();
      }
      for (let y = 0; y <= height; y++) {
        const py = offsetY + y * cellSize;
        surface.beginPath();
        surface.moveTo(offsetX, py);
        surface.lineTo(offsetX + width * cellSize, py);
        surface.strokePath();
      }
      break;
    }
    case 'highlight': {
      surface.fillStyle(0x3b82f6, 0.15);
      for (const cell of path) {
        const x = offsetX + cell.x * cellSize;
        const y = offsetY + cell.y * cellSize;
        surface.fillRect(x, y, cellSize, cellSize);
      }
      surface.lineStyle(1, 0x94a3b8, 0.35);
      for (let x = 0; x <= width; x++) {
        const px = offsetX + x * cellSize;
        surface.beginPath();
        surface.moveTo(px, offsetY);
        surface.lineTo(px, offsetY + height * cellSize);
        surface.strokePath();
      }
      for (let y = 0; y <= height; y++) {
        const py = offsetY + y * cellSize;
        surface.beginPath();
        surface.moveTo(offsetX, py);
        surface.lineTo(offsetX + width * cellSize, py);
        surface.strokePath();
      }
      break;
    }
  }
}

function drawPathOutline(
  surface: TrackDrawSurface,
  path: { x: number; y: number; type: TrackCellType }[],
  toPx: (c: { x: number; y: number }) => { x: number; y: number },
  lineWidth: number,
  cellSize: number,
  options?: { closed?: boolean; color?: number; turnColor?: number }
): void {
  const closed = options?.closed ?? true;
  const n = path.length;
  if (n < 2) return;

  const pts = path.map((c) => toPx(c));
  const r = cellSize * BEZIER_OFFSET;

  const norm = (dx: number, dy: number) => {
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return { x: 0, y: 0 };
    return { x: dx / len, y: dy / len };
  };

  const ax: number[] = [];
  const ay: number[] = [];
  const bx: number[] = [];
  const by: number[] = [];
  const bezierCtrlX: number[] = [];
  const bezierCtrlY: number[] = [];

  for (let j = 0; j < n; j++) {
    const prev = closed ? pts[(j - 1 + n) % n] : pts[Math.max(0, j - 1)];
    const curr = pts[j];
    const next = closed ? pts[(j + 1) % n] : pts[Math.min(n - 1, j + 1)];
    const dIn = norm(curr.x - prev.x, curr.y - prev.y);
    const dOut = norm(next.x - curr.x, next.y - curr.y);
    const distIn = Math.hypot(curr.x - prev.x, curr.y - prev.y);
    const distOut = Math.hypot(next.x - curr.x, next.y - curr.y);
    const clip = Math.min(r, distIn * 0.49, distOut * 0.49);
    ax.push(curr.x - dIn.x * clip);
    ay.push(curr.y - dIn.y * clip);
    bx.push(curr.x + dOut.x * clip);
    by.push(curr.y + dOut.y * clip);
    bezierCtrlX.push(curr.x);
    bezierCtrlY.push(curr.y);
  }

  const segColorAt = (idx: number): number => {
    if (options?.color != null) {
      if (options.turnColor != null && path[idx].type === TrackCellType.TURN) {
        return options.turnColor;
      }
      return options.color;
    }
    return path[idx].type === TrackCellType.TURN ? TURN_LINE_COLOR : STRAIGHT_LINE_COLOR;
  };

  const segCount = closed ? n : n - 1;
  for (let i = 0; i < segCount; i++) {
    const nextIdx = closed ? (i + 1) % n : i + 1;
    const segColor = segColorAt(i);
    const arcColor = segColorAt(nextIdx);

    surface.lineStyle(lineWidth, segColor, 1);
    surface.beginPath();
    surface.moveTo(bx[i], by[i]);
    surface.lineTo(ax[nextIdx], ay[nextIdx]);
    surface.strokePath();

    surface.lineStyle(lineWidth, arcColor, 1);
    surface.beginPath();
    surface.moveTo(ax[nextIdx], ay[nextIdx]);
    drawQuadratic(
      surface,
      ax[nextIdx],
      ay[nextIdx],
      bezierCtrlX[nextIdx],
      bezierCtrlY[nextIdx],
      bx[nextIdx],
      by[nextIdx]
    );
    surface.strokePath();
  }
}

function drawJokerLapBranch(
  surface: TrackDrawSurface,
  circuit: Circuit,
  _offsetX: number,
  _offsetY: number,
  cellSize: number,
  toPx: (c: { x: number; y: number }) => { x: number; y: number },
  lineWidth: number
): void {
  const jl = circuit.jokerLap;
  if (!jl?.cells.length || jl.entryPathIndex < 0 || jl.exitPathIndex < 0) return;

  const entryCell = circuit.path[jl.entryPathIndex];
  const exitCell = circuit.path[jl.exitPathIndex];
  const branchPath = [
    ...(entryCell ? [entryCell] : []),
    ...jl.cells,
    ...(exitCell ? [exitCell] : []),
  ];

  drawPathOutline(surface, branchPath, toPx, lineWidth, cellSize, {
    closed: false,
    color: JOKER_LAP_FILL,
    turnColor: JOKER_LAP_STROKE,
  });
}

function drawStartLaneBranch(
  surface: TrackDrawSurface,
  circuit: Circuit,
  cellSize: number,
  toPx: (c: { x: number; y: number }) => { x: number; y: number },
  lineWidth: number
): void {
  const sl = circuit.startLane;
  if (!sl?.cells.length || sl.joinPathIndex < 0) return;

  const joinCell = circuit.path[sl.joinPathIndex];
  const branchPath = [...(joinCell ? [joinCell] : []), ...sl.cells];

  drawPathOutline(surface, branchPath, toPx, lineWidth, cellSize, {
    closed: false,
    color: START_LANE_FILL,
    turnColor: START_LANE_STROKE,
  });
}

function drawDecorCells(
  surface: TrackDrawSurface,
  circuit: Circuit,
  offsetX: number,
  offsetY: number,
  cellSize: number
): void {
  const decorCells = circuit.decorCells;
  if (!decorCells?.length) return;
  const pad = Math.max(1, cellSize * 0.03);
  for (const cell of decorCells) {
    surface.fillStyle(getDecorCheckerHex(cell.x, cell.y), 0.72);
    surface.fillRect(
      offsetX + cell.x * cellSize + pad,
      offsetY + cell.y * cellSize + pad,
      cellSize - pad * 2,
      cellSize - pad * 2
    );
  }
}

/** Couleurs / bords personnalisés — au-dessus du ruban de piste pour rester visibles en course. */
export function drawCellSurfaceStyles(
  surface: TrackDrawSurface,
  circuit: Circuit,
  offsetX: number,
  offsetY: number,
  cellSize: number
): void {
  const styles = circuit.cellStyles;
  if (!styles) return;
  const pad = Math.max(1, cellSize * 0.015);
  for (const [key, style] of Object.entries(styles)) {
    const [x, y] = key.split(',').map(Number);
    const px = offsetX + x * cellSize;
    const py = offsetY + y * cellSize;
    if (style.fill) {
      if (isDamierSurface(style.fill)) {
        drawDamierCellFill(
          (x, y, w, h, color) => {
            surface.fillStyle(color as number, 0.94);
            surface.fillRect(x, y, w, h);
          },
          px + pad,
          py + pad,
          cellSize - pad * 2,
          x,
          y,
          { useHex: true }
        );
      } else if (isSolidCircuitSurface(style.fill)) {
        surface.fillStyle(SURFACE_FILL_HEX[style.fill], 0.94);
        surface.fillRect(px + pad, py + pad, cellSize - pad * 2, cellSize - pad * 2);
      }
    }
    if (style.edges) {
      drawCircuitCellEdges(
        {
          fillRect: (x, y, w, h, color) => {
            surface.fillStyle(color as number, 1);
            surface.fillRect(x, y, w, h);
          },
        },
        px,
        py,
        cellSize,
        style.edges,
        { useHex: true }
      );
    }
  }
}

/** Rendu identique à TrackRenderer.render(). */
export function renderCircuit(
  surface: TrackDrawSurface,
  circuit: Circuit,
  params: TrackRenderParams
): void {
  if (circuit.path.length === 0) return;

  const gridStyle = params.gridStyle ?? 'checker';
  const { offsetX, offsetY, cellSize } = params;

  drawGrid(surface, circuit, offsetX, offsetY, cellSize, gridStyle);
  drawDecorCells(surface, circuit, offsetX, offsetY, cellSize);
  const cellPx = (cell: { x: number; y: number }) => ({
    x: offsetX + cell.x * cellSize,
    y: offsetY + cell.y * cellSize,
  });
  drawCircuitProps(
    {
      fillRect: (x, y, w, h, color) => {
        if (color.startsWith('#')) {
          const hex = Number.parseInt(color.slice(1), 16);
          surface.fillStyle(hex, 1);
        } else if (color.startsWith('rgba(')) {
          const match = color.match(/rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)/);
          if (match) {
            const hex = (Number(match[1]) << 16) | (Number(match[2]) << 8) | Number(match[3]);
            surface.fillStyle(hex, Number(match[4]));
          } else {
            surface.fillStyle(0xffffff, 1);
          }
        } else {
          surface.fillStyle(0xffffff, 1);
        }
        surface.fillRect(x, y, w, h);
      },
      strokeRect: (x, y, w, h, color, lineWidth) => {
        if (color.startsWith('rgba(')) {
          const match = color.match(/rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)/);
          if (match) {
            const hex = (Number(match[1]) << 16) | (Number(match[2]) << 8) | Number(match[3]);
            surface.lineStyle(lineWidth, hex, Number(match[4]));
          }
        } else {
          const hex = Number.parseInt(color.slice(1), 16);
          surface.lineStyle(lineWidth, hex, 1);
        }
        surface.strokeRect(x, y, w, h);
      },
      fillCircle: (x, y, radius, color) => {
        const hex = Number.parseInt(color.slice(1), 16);
        surface.fillStyle(hex, 1);
        surface.fillRect(x - radius, y - radius, radius * 2, radius * 2);
      },
      drawText: () => {
        /* Texte des panneaux : rendu éditeur / aperçu canvas */
      },
    },
    circuit,
    cellPx,
    cellSize
  );
  const lineWidth = Math.max(8, Math.round(cellSize * PATH_CELL_RATIO));
  const toPx = (cell: { x: number; y: number }) => ({
    x: offsetX + cell.x * cellSize + cellSize / 2,
    y: offsetY + cell.y * cellSize + cellSize / 2,
  });

  drawPathOutline(surface, circuit.path, toPx, lineWidth, cellSize);
  drawJokerLapBranch(surface, circuit, offsetX, offsetY, cellSize, toPx, lineWidth);
  drawStartLaneBranch(surface, circuit, cellSize, toPx, lineWidth);
  drawCellSurfaceStyles(surface, circuit, offsetX, offsetY, cellSize);
}

function colorToRgba(color: number, alpha: number): string {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Adaptateur Canvas 2D pour renderCircuit. */
export class CanvasTrackDrawSurface implements TrackDrawSurface {
  private stroke = '#000';
  private fill = '#000';

  constructor(private readonly ctx: CanvasRenderingContext2D) {}

  lineStyle(width: number, color: number, alpha = 1): void {
    this.stroke = colorToRgba(color, alpha);
    this.ctx.lineWidth = width;
  }

  fillStyle(color: number, alpha = 1): void {
    this.fill = colorToRgba(color, alpha);
  }

  beginPath(): void {
    this.ctx.beginPath();
  }

  moveTo(x: number, y: number): void {
    this.ctx.moveTo(x, y);
  }

  lineTo(x: number, y: number): void {
    this.ctx.lineTo(x, y);
  }

  strokePath(): void {
    this.ctx.strokeStyle = this.stroke;
    this.ctx.stroke();
  }

  fillRect(x: number, y: number, w: number, h: number): void {
    this.ctx.fillStyle = this.fill;
    this.ctx.fillRect(x, y, w, h);
  }

  strokeRect(x: number, y: number, w: number, h: number): void {
    this.ctx.strokeStyle = this.stroke;
    this.ctx.strokeRect(x, y, w, h);
  }

  strokeCircle(x: number, y: number, radius: number): void {
    this.ctx.strokeStyle = this.stroke;
    this.ctx.beginPath();
    this.ctx.arc(x, y, radius, 0, Math.PI * 2);
    this.ctx.stroke();
  }
}

/** Adaptateur Phaser Graphics pour renderCircuit. */
export class PhaserTrackDrawSurface implements TrackDrawSurface {
  constructor(private readonly graphics: Phaser.GameObjects.Graphics) {}

  lineStyle(width: number, color: number, alpha = 1): void {
    this.graphics.lineStyle(width, color, alpha);
  }

  fillStyle(color: number, alpha = 1): void {
    this.graphics.fillStyle(color, alpha);
  }

  beginPath(): void {
    this.graphics.beginPath();
  }

  moveTo(x: number, y: number): void {
    this.graphics.moveTo(x, y);
  }

  lineTo(x: number, y: number): void {
    this.graphics.lineTo(x, y);
  }

  strokePath(): void {
    this.graphics.strokePath();
  }

  fillRect(x: number, y: number, w: number, h: number): void {
    this.graphics.fillRect(x, y, w, h);
  }

  strokeRect(x: number, y: number, w: number, h: number): void {
    this.graphics.strokeRect(x, y, w, h);
  }

  strokeCircle(x: number, y: number, radius: number): void {
    this.graphics.strokeCircle(x, y, radius);
  }
}
